import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useSeo } from '../hooks/useSeo.ts';
import { trackEvent } from '../analytics.ts';

/**
 * Paid-ads landing page for the "tool-intent" keyword cluster — developers and agencies
 * searching for "WCAG scanner", "axe-core report", "automated accessibility testing",
 * "AccessiBe alternative", etc. Tone is more technical, positioning is against overlay
 * vendors and one-off consultant audits.
 */
export default function LandingScannerLP() {
  useSeo({
    title: 'WCAG Scanner That Devs Actually Trust',
    canonicalPath: '/lp/wcag-scanner',
    noindex: true,
  });

  useEffect(() => {
    trackEvent('lp_view', { lp: 'wcag-scanner' });
  }, []);

  return (
    <div className="page lp-page">
      <section className="lp-hero">
        <span className="hero-badge">
          <span className="hero-badge-dot" aria-hidden="true" />
          Real audits. No overlay. No consultant invoice.
        </span>
        <h1>
          The accessibility audit your <span className="accent">developers will actually trust.</span>
        </h1>
        <p className="lead">
          Built on axe-core 4.10 + Playwright. Every violation cited to WCAG 2.1 AA + EN 301 549.
          Plain-English remediations grounded to your real HTML — never invented.
        </p>

        <div className="lp-cta-row">
          <Link
            to="/pricing"
            className="cta"
            onClick={() => trackEvent('lp_cta_click', { lp: 'wcag-scanner', placement: 'hero' })}
          >See pricing — from €29</Link>
          <Link to="/sample" className="cta secondary">See a sample report</Link>
        </div>
        <p className="lp-trust-line">
          <span aria-hidden="true">✓</span> axe-core 4.10
          <span aria-hidden="true"> · </span>
          <span aria-hidden="true">✓</span> Playwright Chromium
          <span aria-hidden="true"> · </span>
          <span aria-hidden="true">✓</span> Multi-page crawl
          <span aria-hidden="true"> · </span>
          <span aria-hidden="true">✓</span> B2B VAT invoicing
        </p>
      </section>

      <section className="lp-fines">
        <h2>Why not an overlay widget?</h2>
        <div className="lp-fines-grid">
          <article>
            <h3>Overlays don't change your code.</h3>
            <p>They modify the page at runtime in the visitor's browser. The underlying HTML is unchanged, so the source-level issues remain.</p>
          </article>
          <article>
            <h3>Real WCAG fixes.</h3>
            <p>We give you a diff your front-end engineers can apply. Code-level remediations, not a JavaScript paint-over.</p>
          </article>
          <article>
            <h3>One €29 audit.</h3>
            <p>Not a recurring overlay subscription with an annual lock-in. Buy a scan, get the report, own it forever.</p>
          </article>
          <article>
            <h3>A documented audit trail.</h3>
            <p>You get a code-level audit report and a draft accessibility statement — the kind of documentation an accessibility review looks for, rather than a runtime script.</p>
          </article>
        </div>
      </section>

      <section className="lp-how">
        <h2>How it works</h2>
        <ol className="lp-steps">
          <li>
            <strong>1. Submit a URL.</strong>
            <span> Our crawler discovers up to 8 same-origin pages via sitemap.xml + in-page link graph.</span>
          </li>
          <li>
            <strong>2. Real browser scan.</strong>
            <span> Playwright (Chromium 1.49) renders each page, injects axe-core, runs every WCAG 2.1 AA rule, and captures a violation-level DOM snippet.</span>
          </li>
          <li>
            <strong>3. AI-grounded remediations.</strong>
            <span> Claude Sonnet 4.6 (temperature 0, 5-layer anti-hallucination guards) writes a plain-English fix grounded to <em>your</em> HTML — not invented.</span>
          </li>
          <li>
            <strong>4. Download the artefacts.</strong>
            <span> Branded PDF audit, EN 301 549-shaped statement HTML, R2-hosted download links. Share with your client or commit to your repo.</span>
          </li>
        </ol>
      </section>

      <section className="lp-pricing">
        <h2>Plans that scale with you</h2>
        <div className="lp-pricing-grid">
          <div className="plan-card">
            <h3>Single scan</h3>
            <p className="price">€29<span> one-time</span></p>
            <p className="muted">For "I just need this one site audited."</p>
            <Link
              to="/pricing"
              className="cta"
              onClick={() => trackEvent('lp_cta_click', { lp: 'wcag-scanner', placement: 'pricing_single' })}
            >Buy single scan</Link>
          </div>
          <div className="plan-card">
            <h3>Basic</h3>
            <p className="price">€9<span>/mo</span></p>
            <p className="muted">5 scans/month — re-audit on every release.</p>
            <Link
              to="/pricing"
              className="cta"
              onClick={() => trackEvent('lp_cta_click', { lp: 'wcag-scanner', placement: 'pricing_basic' })}
            >Start Basic</Link>
          </div>
          <div className="plan-card plan-card-featured">
            <p className="plan-badge">For agencies</p>
            <h3>Agency</h3>
            <p className="price">€49<span>/mo</span></p>
            <p className="muted">50 scans, your branding on reports, B2B invoicing.</p>
            <Link
              to="/pricing"
              className="cta"
              onClick={() => trackEvent('lp_cta_click', { lp: 'wcag-scanner', placement: 'pricing_agency' })}
            >Start Agency</Link>
          </div>
        </div>
      </section>

      <section className="lp-final-cta">
        <h2>See a sample report before you buy</h2>
        <p className="lead">A real audit, ready in your browser. Decide if the format works for your team — then come back and run yours.</p>
        <div className="lp-cta-row">
          <Link to="/sample" className="cta secondary">View sample audit</Link>
          <Link
            to="/pricing"
            className="cta"
            onClick={() => trackEvent('lp_cta_click', { lp: 'wcag-scanner', placement: 'final' })}
          >Run my own — €29</Link>
        </div>
      </section>
    </div>
  );
}
